import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SchoolHoliday } from '@/mocks/calendarEntries';

interface HolidayMasterProps {
  holidays: SchoolHoliday[];
  onAdd: (holiday: Omit<SchoolHoliday, 'id' | 'color' | 'type'>) => void;
  onEdit: (id: string, holiday: Omit<SchoolHoliday, 'id' | 'color' | 'type'>) => void;
  onDelete: (id: string) => void;
}

function isOverlapping(h1: SchoolHoliday, startDate: string, endDate: string, excludeId?: string): boolean {
  const s1 = new Date(h1.startDate);
  const e1 = new Date(h1.endDate);
  const s2 = new Date(startDate);
  const e2 = new Date(endDate);
  if (excludeId && h1.id === excludeId) return false;
  return s1 <= e2 && s2 <= e1;
}

export default function HolidayMaster({ holidays, onAdd, onEdit, onDelete }: HolidayMasterProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    holidayTitle: '',
    startDate: '',
    endDate: '',
    holidayType: 'National' as 'National' | 'Regional' | 'Discretionary',
    impactsAttendance: true,
    description: '',
  });

  const resetForm = () => {
    setFormData({ holidayTitle: '', startDate: '', endDate: '', holidayType: 'National', impactsAttendance: true, description: '' });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleSubmit = () => {
    if (!formData.holidayTitle.trim() || !formData.startDate || !formData.endDate) return;
    if (formData.endDate < formData.startDate) {
      setError('End date must be after start date');
      return;
    }
    // Check duplicate range
    const overlapping = holidays.some((h) => isOverlapping(h, formData.startDate, formData.endDate, editingId ?? undefined));
    if (overlapping) {
      setError(t('dashboard.duplicateDateRange'));
      return;
    }

    const payload = { ...formData, holidayTitle: formData.holidayTitle.trim(), description: formData.description.trim() };
    if (editingId) {
      onEdit(editingId, payload);
    } else {
      onAdd(payload);
    }
    resetForm();
  };

  const startEdit = (holiday: SchoolHoliday) => {
    setFormData({
      holidayTitle: holiday.holidayTitle,
      startDate: holiday.startDate,
      endDate: holiday.endDate,
      holidayType: holiday.holidayType,
      impactsAttendance: holiday.impactsAttendance,
      description: holiday.description || '',
    });
    setEditingId(holiday.id);
    setShowForm(true);
  };

  const formatDateRange = (start: string, end: string): string => {
    const s = new Date(start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    if (start === end) return s;
    const e = new Date(end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${s} — ${e}`;
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      National: 'bg-red-100 text-red-700',
      Regional: 'bg-amber-100 text-amber-700',
      Discretionary: 'bg-orange-100 text-orange-700',
    };
    return colors[type] || 'bg-background-200 text-foreground-500';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-sm text-foreground-500 font-label">{t('dashboard.holidayMasterDescription')}</p>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line text-base" />
          {t('dashboard.addHoliday')}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb-4 p-4 bg-background-100 rounded-xl border border-background-200/70">
          <h4 className="font-label font-semibold text-foreground-800 text-sm mb-3">
            {editingId ? t('dashboard.editHoliday') : t('dashboard.addHoliday')}
          </h4>
          {error && (
            <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 font-label">{error}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.holidayTitle')} *</label>
              <input
                type="text"
                value={formData.holidayTitle}
                onChange={(e) => setFormData((p) => ({ ...p, holidayTitle: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900"
                placeholder="e.g. Winter Vacation"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.startDate')} *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData((p) => ({ ...p, startDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900 cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.endDate')} *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData((p) => ({ ...p, endDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900 cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.holidayType')}</label>
              <select
                value={formData.holidayType}
                onChange={(e) => setFormData((p) => ({ ...p, holidayType: e.target.value as 'National' | 'Regional' | 'Discretionary' }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900 cursor-pointer"
              >
                <option value="National">{t('dashboard.holidayNational')}</option>
                <option value="Regional">{t('dashboard.holidayRegional')}</option>
                <option value="Discretionary">{t('dashboard.holidayDiscretionary')}</option>
              </select>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.impactsAttendance}
                  onChange={(e) => setFormData((p) => ({ ...p, impactsAttendance: e.target.checked }))}
                  className="w-4 h-4 rounded border-2 border-background-300 cursor-pointer accent-primary-500"
                />
                <span className="text-sm font-label text-foreground-700">{t('dashboard.impactsAttendanceLabel')}</span>
              </label>
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.eventDescription')}</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900"
                placeholder="Brief description..."
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSubmit}
              disabled={!formData.holidayTitle.trim() || !formData.startDate || !formData.endDate}
              className="px-4 py-2 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              {editingId ? t('common.save') : t('common.add')}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-sm font-label font-medium text-foreground-500 hover:text-foreground-700 rounded-lg cursor-pointer whitespace-nowrap">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {holidays.length === 0 ? (
        <div className="text-center py-10 text-foreground-400 text-sm font-label">{t('dashboard.noHolidaysDefined')}</div>
      ) : (
        <div className="overflow-x-auto border border-background-200/70 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">#ID</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.holidayTitle')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.startDate')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.endDate')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.holidayType')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.impactsAttendanceLabel')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id} className="border-t border-background-200/70 hover:bg-background-50/50 transition-colors">
                  <td className="px-4 py-3 text-foreground-500 font-mono text-xs">{h.id}</td>
                  <td className="px-4 py-3 text-foreground-800 font-label font-medium">{h.holidayTitle}</td>
                  <td className="px-4 py-3 text-foreground-600 font-label text-xs whitespace-nowrap">{new Date(h.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td className="px-4 py-3 text-foreground-600 font-label text-xs whitespace-nowrap">{new Date(h.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-label font-medium ${getTypeBadge(h.holidayType)}`}>
                      {t(`dashboard.holiday${h.holidayType}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-label font-medium ${h.impactsAttendance ? 'bg-amber-100 text-amber-700' : 'bg-background-200 text-foreground-500'}`}>
                      {h.impactsAttendance ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(h)} className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer" title={t('common.edit')}>
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      <button onClick={() => onDelete(h.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title={t('common.delete')}>
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}